import { c as _c } from "react/compiler-runtime";
import { isCliTokenExpired } from '../../../../../../../../../domain/cli-token-access';
import { APIAccountSession, APIAccountSessionList } from '../../../../../../../../../typings/AccountSession.interface';
import { formatDate } from '../../../../../../utils/date';
import { getCliKeyAccess } from '../../../../../common/ApiKeys/CliKeyAccess';
import Badge from '../../../../../common/base/Badge';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Icon from '../../../../../common/base/Icon';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import { Div, Span } from '../../../../../common/helpers/StyledUtils';
import { useDatePreferences } from '../../../../../hooks/useDatePreferences';
import { CliCommand, getClientLabel, getSessionName, useNow } from './utils';
interface SessionsSectionProps {
  sessions?: APIAccountSessionList;
  has_error: boolean;
  organization_names: Map<string, string>;
  project_names: Map<string, string>;
  onRevoke: (session: APIAccountSession) => void;
  onRevokeAll: () => void;
}

// The device gets the free width. The last track fits the date and the revoke button.
const GRID = {
  columns: 'minmax(0, 1fr) minmax(0, 200px)',
  gap: 3
};

// The icon says whether it's a browser or a CLI. Hovering it names the browser.
const CLIENT_ICONS: Record<APIAccountSession['client'], 'computer' | 'terminal'> = {
  dashboard: 'computer',
  cli: 'terminal'
};
const SessionsSection: React.FC<SessionsSectionProps> = t0 => {
  const $ = _c(22);
  const {
    sessions,
    has_error,
    organization_names,
    project_names,
    onRevoke,
    onRevokeAll
  } = t0;
  const {
    timezone
  } = useDatePreferences();
  const now = useNow();
  let t1;
  if ($[0] !== sessions?.models) {
    t1 = sessions?.models.some(_temp);
    $[0] = sessions?.models;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const has_others = !!t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      b: 10
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 4
    };
    t4 = {
      justify: "space-between",
      align: "center",
      gap: 4
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "center",
      gap: 2
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Text bold={true} size="l" as="h2" m={{
      y: 0
    }}>Sessions</Text>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Div flex={t5}>{t6}<Tooltip tooltip={<>Everywhere you're logged in: browsers on the dashboard, and every{" "}<CliCommand>hookdeck login</CliCommand>.</>}><Icon icon="info" muted={true} /></Tooltip></Div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = !has_others;
  let t9;
  if ($[8] !== onRevokeAll || $[9] !== t8) {
    t9 = <Div m={t3} flex={t4}>{t7}<Button outline={true} danger={true} icon="logout" disabled={t8} onClick={onRevokeAll}>Revoke all</Button></Div>;
    $[8] = onRevokeAll;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== has_error || $[12] !== now || $[13] !== onRevoke || $[14] !== organization_names || $[15] !== project_names || $[16] !== sessions || $[17] !== timezone) {
    t10 = has_error && !sessions ? <Text danger={true}>Your sessions could not be loaded, please refresh the page.</Text> : <Loading require={[sessions]}>{() => <StyledCard overflow_hidden={true}><StyledCardSection muted={true} p={{
          x: 4
        }}><Div grid={GRID}>{["Device", "Logged in"].map(_temp2)}</Div></StyledCardSection>{sessions.models.map(session_0 => {
          const access = session_0.organization_id ? getCliKeyAccess(session_0, organization_names, project_names) : null;
          return <StyledCardSection key={session_0.id} data-testid="session-row" p={{
            x: 4
          }} grid={GRID}><Div min_w={0} p={{
              y: 3
            }} flex={{
              align: "center",
              gap: 2,
              wrap: true
            }}><Tooltip tooltip={getClientLabel(session_0)}><Span role="img" aria-label={getClientLabel(session_0)} flex={{
                  align: "center"
                }}><Icon icon={CLIENT_ICONS[session_0.client]} muted={true} /></Span></Tooltip><Text semi={true} size="s" overflow_wrap="anywhere">{getSessionName(session_0)}</Text>{session_0.is_current && <Tooltip tooltip="This browser. Log out to end this session."><Span role="img" aria-label="This browser" data-testid="this-browser" flex={{
                  align: "center"
                }}><Icon icon="success" success={true} /></Span></Tooltip>}{session_0.is_listening && <Badge subtle={true} success={true}>Active now</Badge>}{access && <Tooltip tooltip={access.details} disabled={!access.details}><Badge neutral={true} subtle={true}>{access.label}</Badge></Tooltip>}{session_0.expires_at && (isCliTokenExpired(session_0, now) ? <Badge subtle={true} danger={true}>Expired</Badge> : <Badge neutral={true} subtle={true}>Expires {formatDate(session_0.expires_at, "MMM d, yyyy", timezone)}</Badge>)}</Div><Div min_w={0} flex={{
              align: "center",
              justify: "space-between",
              gap: 2
            }}><Text size="s" muted={!session_0.logged_in_at} ellipsis={true}>{session_0.logged_in_at ? formatDate(session_0.logged_in_at, "MMM d, yyyy, h:mm a", timezone) : session_0.last_active_at ? `Last active ${formatDate(session_0.last_active_at, "MMM d, yyyy", timezone)}` : "Unknown"}</Text>{!session_0.is_current && <Tooltip tooltip="Revoke session" align="right"><Button ghost={true} icon="delete" aria-label="Revoke" onClick={() => onRevoke(session_0)} /></Tooltip>}</Div></StyledCardSection>;
        })}{sessions.has_more && <StyledCardSection p={{
          x: 4,
          y: 3
        }}><Text size="s" muted={true}>Showing your newest sessions. Revoke all also logs out the older ones.</Text></StyledCardSection>}</StyledCard>}</Loading>;
    $[11] = has_error;
    $[12] = now;
    $[13] = onRevoke;
    $[14] = organization_names;
    $[15] = project_names;
    $[16] = sessions;
    $[17] = timezone;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <Div m={t2}>{t9}{t10}</Div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
};
export default SessionsSection;
function _temp(session) {
  return !session.is_current;
}
function _temp2(heading) {
  return <Text key={heading} muted={true} semi={true} size="xs" p={{
    y: 1
  }}>{heading}</Text>;
}